import React, { useEffect, useState } from "react";
import api, { formatApiError, asArray } from "../../lib/api";
import FileUpload from "./FileUpload";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({
    title_ar: "", title_en: "", slug: "",
    category_ar: "", category_en: "",
    client_ar: "", client_en: "",
    city_ar: "", city_en: "",
    year: "", cover_image: "",
    gallery: [], partner_logos: [],
    objective_ar: "", objective_en: "",
    challenge_ar: "", challenge_en: "",
    methodology_ar: "", methodology_en: "",
    achievements_ar: "", achievements_en: "",
    stats: [],
    featured: false, published: true, order: 0,
});

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
const inputCls = "w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2";
const textareaCls = "w-full bg-white border border-black/15 focus:border-[#0060A7] outline-none p-3 text-sm";

export default function AdminWorks() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/works");
            setItems(asArray(data, "works"));
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = { ...editing };
            payload.order = Number(payload.order) || 0;
            if (payload.id) {
                await api.patch(`/admin/works/${payload.id}`, payload);
            } else {
                await api.post("/admin/works", payload);
            }
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete?")) return;
        try {
            await api.delete(`/admin/works/${id}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (editing) {
        const e = editing;
        return (
            <div data-testid="admin-work-form">
                <button onClick={() => setEditing(null)} className="text-sm text-[#6B7280] mb-6 inline-flex items-center gap-2">
                    <X size={14} /> Cancel
                </button>
                <h2 className="heading text-3xl tracking-tight mb-8">{e.id ? "Edit Work" : "New Work"}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8">
                    <Field label="Slug (URL)"><input data-testid="work-slug" className={inputCls} value={e.slug} onChange={(ev) => setEditing({ ...e, slug: ev.target.value })} /></Field>
                    <Field label="Year"><input className={inputCls} value={e.year} onChange={(ev) => setEditing({ ...e, year: ev.target.value })} /></Field>
                    <Field label="Title (AR)"><input data-testid="work-title-ar" className={inputCls} value={e.title_ar} onChange={(ev) => setEditing({ ...e, title_ar: ev.target.value })} /></Field>
                    <Field label="Title (EN)"><input data-testid="work-title-en" className={inputCls} value={e.title_en} onChange={(ev) => setEditing({ ...e, title_en: ev.target.value })} /></Field>
                    <Field label="Category (AR)"><input className={inputCls} value={e.category_ar} onChange={(ev) => setEditing({ ...e, category_ar: ev.target.value })} /></Field>
                    <Field label="Category (EN)"><input className={inputCls} value={e.category_en} onChange={(ev) => setEditing({ ...e, category_en: ev.target.value })} /></Field>
                    <Field label="Client (AR)"><input className={inputCls} value={e.client_ar} onChange={(ev) => setEditing({ ...e, client_ar: ev.target.value })} /></Field>
                    <Field label="Client (EN)"><input className={inputCls} value={e.client_en} onChange={(ev) => setEditing({ ...e, client_en: ev.target.value })} /></Field>
                    <Field label="City (AR)"><input className={inputCls} value={e.city_ar} onChange={(ev) => setEditing({ ...e, city_ar: ev.target.value })} /></Field>
                    <Field label="City (EN)"><input className={inputCls} value={e.city_en} onChange={(ev) => setEditing({ ...e, city_en: ev.target.value })} /></Field>

                    <div className="md:col-span-2">
                        <FileUpload value={e.cover_image} onChange={(v) => setEditing({ ...e, cover_image: v })} label="Cover Image" testid="work-cover-upload" />
                    </div>

                    <div className="md:col-span-2 border-t border-black/10 pt-6" data-testid="work-partner-logos-editor">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-4">
                            Partner logos (1-3 logos shown above title on detail page, separated by " | ")
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(e.partner_logos || []).map((img, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <div className="flex-1">
                                        <FileUpload
                                            value={img}
                                            onChange={(v) => {
                                                const arr = [...e.partner_logos];
                                                arr[i] = v;
                                                setEditing({ ...e, partner_logos: arr });
                                            }}
                                            label={`Logo ${i + 1}`}
                                            testid={`work-partner-${i}`}
                                        />
                                    </div>
                                    <button type="button"
                                        onClick={() => setEditing({ ...e, partner_logos: e.partner_logos.filter((_, j) => j !== i) })}
                                        className="text-red-500 mt-6"
                                        data-testid={`work-partner-delete-${i}`}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {(e.partner_logos || []).length < 3 && (
                            <button type="button"
                                onClick={() => setEditing({ ...e, partner_logos: [...(e.partner_logos || []), ""] })}
                                data-testid="work-partner-add"
                                className="mt-4 text-sm inline-flex items-center gap-2 text-[#0060A7]">
                                <Plus size={14} /> Add partner logo
                            </button>
                        )}
                    </div>

                    <div className="md:col-span-2 border-t border-black/10 pt-6" data-testid="work-gallery-editor">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-4">Gallery (additional images)</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(e.gallery || []).map((img, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <div className="flex-1">
                                        <FileUpload
                                            value={img}
                                            onChange={(v) => {
                                                const gallery = [...e.gallery];
                                                gallery[i] = v;
                                                setEditing({ ...e, gallery });
                                            }}
                                            label={`Image ${i + 1}`}
                                            testid={`work-gallery-${i}`}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setEditing({ ...e, gallery: e.gallery.filter((_, j) => j !== i) })}
                                        className="text-red-500 mt-6"
                                        data-testid={`work-gallery-delete-${i}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => setEditing({ ...e, gallery: [...(e.gallery || []), ""] })}
                            data-testid="work-gallery-add"
                            className="mt-4 text-sm inline-flex items-center gap-2 text-[#0060A7]"
                        >
                            <Plus size={14} /> Add gallery image
                        </button>
                    </div>

                    <Field label="Objective (AR)"><textarea rows={3} className={textareaCls} value={e.objective_ar} onChange={(ev) => setEditing({ ...e, objective_ar: ev.target.value })} /></Field>
                    <Field label="Objective (EN)"><textarea rows={3} className={textareaCls} value={e.objective_en} onChange={(ev) => setEditing({ ...e, objective_en: ev.target.value })} /></Field>
                    <Field label="Challenge (AR)"><textarea rows={3} className={textareaCls} value={e.challenge_ar} onChange={(ev) => setEditing({ ...e, challenge_ar: ev.target.value })} /></Field>
                    <Field label="Challenge (EN)"><textarea rows={3} className={textareaCls} value={e.challenge_en} onChange={(ev) => setEditing({ ...e, challenge_en: ev.target.value })} /></Field>
                    <Field label="Methodology (AR)"><textarea rows={3} className={textareaCls} value={e.methodology_ar} onChange={(ev) => setEditing({ ...e, methodology_ar: ev.target.value })} /></Field>
                    <Field label="Methodology (EN)"><textarea rows={3} className={textareaCls} value={e.methodology_en} onChange={(ev) => setEditing({ ...e, methodology_en: ev.target.value })} /></Field>
                    <Field label="Achievements (AR)"><textarea rows={3} className={textareaCls} value={e.achievements_ar} onChange={(ev) => setEditing({ ...e, achievements_ar: ev.target.value })} /></Field>
                    <Field label="Achievements (EN)"><textarea rows={3} className={textareaCls} value={e.achievements_en} onChange={(ev) => setEditing({ ...e, achievements_en: ev.target.value })} /></Field>

                    <div className="md:col-span-2 border-t border-black/10 pt-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-4">Stats</div>
                        {e.stats.map((s, i) => (
                            <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                <input placeholder="Label AR" className={inputCls} value={s.label_ar} onChange={(ev) => {
                                    const stats = [...e.stats]; stats[i].label_ar = ev.target.value; setEditing({ ...e, stats });
                                }} />
                                <input placeholder="Label EN" className={inputCls} value={s.label_en} onChange={(ev) => {
                                    const stats = [...e.stats]; stats[i].label_en = ev.target.value; setEditing({ ...e, stats });
                                }} />
                                <div className="flex gap-2">
                                    <input placeholder="Value (e.g. 1.7B)" className={inputCls} value={s.value} onChange={(ev) => {
                                        const stats = [...e.stats]; stats[i].value = ev.target.value; setEditing({ ...e, stats });
                                    }} />
                                    <button type="button" onClick={() => setEditing({ ...e, stats: e.stats.filter((_, j) => j !== i) })} className="text-red-500"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={() => setEditing({ ...e, stats: [...e.stats, { label_ar: "", label_en: "", value: "" }] })} className="text-sm inline-flex items-center gap-2 text-[#0060A7]">
                            <Plus size={14} /> Add stat
                        </button>
                    </div>

                    <div className="md:col-span-2 flex flex-wrap items-center gap-6 border-t border-black/10 pt-6">
                        <label className="inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={e.featured} onChange={(ev) => setEditing({ ...e, featured: ev.target.checked })} />
                            Featured
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={e.published} onChange={(ev) => setEditing({ ...e, published: ev.target.checked })} />
                            Published
                        </label>
                        <Field label="Order"><input type="number" className={inputCls} value={e.order} onChange={(ev) => setEditing({ ...e, order: ev.target.value })} /></Field>
                    </div>
                </div>
                <div className="mt-6 flex gap-3">
                    <button onClick={save} data-testid="work-save" className="bg-[#002951] hover:bg-[#0060A7] text-white px-8 py-3 text-sm uppercase tracking-[0.2em]">Save</button>
                    <button onClick={() => setEditing(null)} className="border border-black/15 px-8 py-3 text-sm uppercase tracking-[0.2em]">Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-works-page">
            <div className="flex items-center justify-between mb-6">
                <h1 className="heading text-3xl tracking-tight">Works</h1>
                <button onClick={() => setEditing(blank())} data-testid="add-work-btn" className="bg-[#002951] hover:bg-[#0060A7] text-white px-5 py-2.5 text-sm inline-flex items-center gap-2">
                    <Plus size={14} /> New Work
                </button>
            </div>
            <div className="bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-[#EFF0F1] text-left text-xs uppercase tracking-[0.2em] text-[#6B7280]">
                        <tr><th className="p-4">Title</th><th className="p-4">Slug</th><th className="p-4">Year</th><th className="p-4">Status</th><th className="p-4"></th></tr>
                    </thead>
                    <tbody>
                        {items.map((w) => (
                            <tr key={w.id} className="border-t border-black/5" data-testid={`work-row-${w.slug}`}>
                                <td className="p-4">
                                    <div className="heading text-base">{w.title_en}</div>
                                    <div className="text-xs text-[#6B7280]">{w.title_ar}</div>
                                </td>
                                <td className="p-4 text-[#6B7280]">{w.slug}</td>
                                <td className="p-4 text-[#6B7280]">{w.year}</td>
                                <td className="p-4">
                                    {w.featured && <span className="text-[10px] uppercase tracking-[0.2em] bg-[#0060A7] text-white px-2 py-0.5 mr-1">Featured</span>}
                                    {w.published ? <span className="text-emerald-600 text-xs">Published</span> : <span className="text-[#6B7280] text-xs">Draft</span>}
                                </td>
                                <td className="p-4 text-right">
                                    <button onClick={() => setEditing(w)} className="mr-3 text-[#0060A7]" data-testid={`edit-work-${w.slug}`}><Edit3 size={16} /></button>
                                    <button onClick={() => remove(w.id)} className="text-red-500" data-testid={`delete-work-${w.slug}`}><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
